import { useState, useRef, useId } from "react";
import { POSITIONS, inputStyle } from "../constants";

// ── Tactical Board ────────────────────────────────────────────────────────
// Drag-and-drop field diagram for coaches to design plays. `mode="edit"`
// renders the draggable editor with title/description + save/cancel;
// `mode="view"` renders a static read-only diagram (used by players).

const FIELD_GREEN = "#173626";
const DIRT_TAN     = "#c9a06c";
const DIRT_STROKE  = "#a3803f";
const BASE_FILL    = "#f6f2e8";
const BOUNDARY     = "rgba(255,255,255,0.32)";
const FONT_STACK   = "'Helvetica Neue', Arial, sans-serif";

const BASE_XY = { first:{x:70,y:55}, second:{x:50,y:35}, third:{x:30,y:55} };
const RUNNER_OPTS = [{ id:"first", label:"1st" }, { id:"second", label:"2nd" }, { id:"third", label:"3rd" }];

const defaultDefense = () => POSITIONS.map(p => ({ id:p.id, type:"defense", label:p.short, x:p.x, y:p.y }));
const defaultBatter  = () => ({ id:"batter", type:"batter", label:"B", x:50, y:76 });
const defaultBall    = () => ({ id:"ball", type:"ball", label:"⚾", x:50, y:66 });

const emptyPlay = () => ({
  title:"", description:"",
  markers:[...defaultDefense(), defaultBatter()],
  arrows:[],
});

export function TacticalBoard({ mode="edit", initial, onSave, onCancel }) {
  const isEdit = mode==="edit";
  const [play, setPlay] = useState(() => ({ ...emptyPlay(), ...(initial||{}) }));
  const [error, setError] = useState(null);
  const [arrowMode, setArrowMode] = useState(false);
  const [arrowStart, setArrowStart] = useState(null);
  const [dragId, setDragId] = useState(null);
  const svgRef = useRef(null);
  const uid = useId();

  const activeRunners = RUNNER_OPTS.filter(r => play.markers.some(m => m.id===`runner-${r.id}`));
  const ballOn = play.markers.some(m => m.id==="ball");

  const toCoords = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.min(97, Math.max(3, ((clientX-rect.left)/rect.width)*100));
    const y = Math.min(97, Math.max(3, ((clientY-rect.top)/rect.height)*100));
    return { x:parseFloat(x.toFixed(1)), y:parseFloat(y.toFixed(1)) };
  };

  const handlePointerDown = (e, id) => {
    if (!isEdit) return;
    e.stopPropagation();
    svgRef.current.setPointerCapture(e.pointerId);
    setDragId(id);
  };
  const handlePointerMove = e => {
    if (!isEdit || !dragId) return;
    const { x, y } = toCoords(e.clientX, e.clientY);
    setPlay(prev => ({ ...prev, markers: prev.markers.map(m => m.id===dragId?{...m,x,y}:m) }));
  };
  const handlePointerUp = () => setDragId(null);

  const handleFieldClick = e => {
    if (!isEdit || !arrowMode) return;
    const { x, y } = toCoords(e.clientX, e.clientY);
    if (!arrowStart) { setArrowStart({ x, y }); return; }
    setPlay(prev => ({ ...prev, arrows:[...prev.arrows, { ...arrowStart, x2:x, y2:y }] }));
    setArrowStart(null);
  };

  const toggleRunner = id => setPlay(prev => {
    const mid = `runner-${id}`;
    const exists = prev.markers.some(m => m.id===mid);
    return { ...prev, markers: exists ? prev.markers.filter(m=>m.id!==mid) : [...prev.markers, { id:mid, type:"runner", label:RUNNER_OPTS.find(r=>r.id===id).label, ...BASE_XY[id] }] };
  });

  const toggleBall = () => setPlay(prev => {
    const exists = prev.markers.some(m => m.id==="ball");
    return { ...prev, markers: exists ? prev.markers.filter(m=>m.id!=="ball") : [...prev.markers, defaultBall()] };
  });

  const resetPositions = () => setPlay(prev => ({
    ...prev,
    markers: [
      ...defaultDefense(), defaultBatter(),
      ...prev.markers.filter(m=>m.type==="runner").map(m=>({ ...m, ...BASE_XY[m.id.replace("runner-","")] })),
      ...(prev.markers.some(m=>m.id==="ball") ? [defaultBall()] : []),
    ],
  }));

  const removeArrow = i => setPlay(prev => ({ ...prev, arrows: prev.arrows.filter((_,idx)=>idx!==i) }));

  const handleSave = () => {
    if (!play.title.trim()) return setError("Give the play a short title.");
    setError(null);
    onSave(play);
  };

  return (
    <div style={{background:"white",border:isEdit?"1.5px solid #231F20":"none",borderRadius:10,padding:isEdit?"16px":0,marginBottom:isEdit?14:0}}>
      {isEdit && <div style={{fontSize:14,fontWeight:700,color:"#231F20",marginBottom:12}}>{initial?"Edit play":"New play"}</div>}

      {isEdit && (<>
        <label style={labelStyle}>Title</label>
        <input style={inputStyle} value={play.title} onChange={e=>setPlay(p=>({...p,title:e.target.value}))} placeholder="e.g. Cutoff on a double to the gap"/>
        <label style={labelStyle}>Description (optional)</label>
        <textarea style={{...inputStyle,minHeight:50}} value={play.description} onChange={e=>setPlay(p=>({...p,description:e.target.value}))} placeholder="Explain the play, when to use it..."/>
      </>)}

      {!isEdit && play.title && <div style={{fontSize:14,fontWeight:700,color:"#231F20",marginBottom:4}}>{play.title}</div>}
      {!isEdit && play.description && <div style={{fontSize:12,color:"#666",marginBottom:10,lineHeight:1.5}}>{play.description}</div>}

      {isEdit && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {RUNNER_OPTS.map(r => (
            <button key={r.id} type="button" onClick={()=>toggleRunner(r.id)} style={pillStyle(activeRunners.some(a=>a.id===r.id))}>{r.label} Runner</button>
          ))}
          <button type="button" onClick={toggleBall} style={pillStyle(ballOn)}>⚾ Ball</button>
          <button type="button" onClick={()=>{setArrowMode(a=>!a);setArrowStart(null);}} style={pillStyle(arrowMode)}>{arrowMode?(arrowStart?"Click end point…":"Click start point…"):"➜ Add Arrow"}</button>
          <button type="button" onClick={resetPositions} style={pillStyle(false)}>↺ Reset</button>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox="0 0 100 100"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={handleFieldClick}
        style={{display:"block",width:"100%",maxWidth:340,height:"auto",margin:"0 auto",background:FIELD_GREEN,borderRadius:8,touchAction:"none",cursor:isEdit&&arrowMode?"crosshair":"default"}}
      >
        <defs>
          <marker id={`${uid}-arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#1FD71D"/>
          </marker>
          <filter id={`${uid}-shadow`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0.6" stdDeviation="0.7" floodColor="#000" floodOpacity="0.4"/>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="none" stroke={BOUNDARY} strokeWidth="0.6"/>
        <line x1="50" y1="83" x2="3" y2="3" stroke={BOUNDARY} strokeWidth="0.6"/>
        <line x1="50" y1="83" x2="97" y2="3" stroke={BOUNDARY} strokeWidth="0.6"/>
        <circle cx="50" cy="57" r="29" fill={DIRT_TAN}/>
        <polygon points="50,34 74,57 50,80 26,57" fill={FIELD_GREEN}/>
        <circle cx="50" cy="58" r="3" fill={DIRT_TAN} stroke={DIRT_STROKE} strokeWidth="0.4"/>
        {["first","second","third"].map(b => (
          <rect key={b} x={BASE_XY[b].x-2.6} y={BASE_XY[b].y-2.6} width="5.2" height="5.2" fill={BASE_FILL} stroke="rgba(0,0,0,0.25)" strokeWidth="0.4" transform={`rotate(45,${BASE_XY[b].x},${BASE_XY[b].y})`} filter={`url(#${uid}-shadow)`}/>
        ))}

        {play.arrows.map((a,i) => (
          <line key={i} x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke="#1FD71D" strokeWidth="1.2" markerEnd={`url(#${uid}-arrow)`}/>
        ))}
        {arrowStart && <circle cx={arrowStart.x} cy={arrowStart.y} r="1.5" fill="#1FD71D"/>}

        {play.markers.map(m => {
          const isDefense = m.type==="defense";
          const fill = isDefense ? "#2f7bdb" : m.type==="runner" ? "#1FD71D" : m.type==="batter" ? BASE_FILL : "#e0e0e0";
          const stroke = isDefense ? "#1a5cae" : m.type==="runner" ? "#14a616" : "rgba(0,0,0,0.2)";
          const r = m.type==="ball" ? 3 : 6;
          return (
            <g key={m.id} onPointerDown={e=>handlePointerDown(e,m.id)} style={{cursor:isEdit?"grab":"default"}}>
              <circle cx={m.x} cy={m.y} r={r} fill={fill} stroke={stroke} strokeWidth="0.8" filter={`url(#${uid}-shadow)`}/>
              <text x={m.x} y={m.y+1.2} textAnchor="middle" dominantBaseline="middle" fontSize={m.type==="ball"?"3":"3"} fill={isDefense?"white":"#111"} fontWeight="800" style={{pointerEvents:"none",fontFamily:FONT_STACK}}>{m.label}</text>
            </g>
          );
        })}
      </svg>

      {isEdit && play.arrows.length>0 && (
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
          {play.arrows.map((a,i) => (
            <button key={i} type="button" onClick={()=>removeArrow(i)} style={{fontSize:10,color:"#e53935",background:"none",border:"1px solid #e53935",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Remove arrow {i+1} ✕</button>
          ))}
        </div>
      )}

      {isEdit && error && <div style={{color:"#e53935",fontSize:12,margin:"10px 0 0"}}>{error}</div>}

      {isEdit && (
        <div style={{display:"flex",gap:8,marginTop:14}}>
          <button onClick={handleSave} style={{flex:1,padding:"11px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace"}}>Save Play</button>
          <button onClick={onCancel} style={{padding:"11px 16px",background:"white",color:"#888",border:"1px solid #ddd",borderRadius:7,cursor:"pointer",fontSize:13}}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display:"block", fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:5 };

const pillStyle = active => ({
  padding:"7px 12px", borderRadius:999, border:active?"2px solid #231F20":"1px solid #ddd",
  background:active?"#231F20":"white", color:active?"#1FD71D":"#333", cursor:"pointer",
  fontSize:11, fontWeight:700, fontFamily:"monospace",
});
